import type { BackstageIdentityApi } from '@backstage/core-plugin-api';
import { milliseconds } from 'date-fns';

// Parses supplied JWT token and returns the payload
function parseJwt(token: string): { exp: number } {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(
        c =>
          // eslint-disable-next-line prefer-template
          '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2),
      )
      .join(''),
  );

  return JSON.parse(jsonPayload);
}

// Returns milliseconds until the supplied JWT token expires
function msUntilExpiry(token: string): number {
  const payload = parseJwt(token);
  const remaining =
    new Date(payload.exp * 1000).getTime() - new Date().getTime();
  return remaining;
}

/**
 * Calls the specified url regularly using an auth token to set a token cookie
 * @param url The url to call
 * @param backstageIdentityApi The backstage identity API
 */
export async function scheduleTokenCookieRefresh(
  url: string,
  backstageIdentityApi: BackstageIdentityApi,
) {
  const identity = await backstageIdentityApi.getBackstageIdentity();
  if (!identity) {
    return;
  }

  const { token } = identity;
  await fetch(url, {
    mode: 'cors',
    credentials: 'include',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  // Call this function again a few minutes before the token expires
  const ms = msUntilExpiry(token) - milliseconds({ minutes: 2 });
  setTimeout(
    () => {
      scheduleTokenCookieRefresh(url, backstageIdentityApi);
    },
    ms > 0 ? ms : 10000,
  );
}
